import React from "react";
import { Form, Formik, FormikConfig } from "formik";
import { object } from "yup";

import { useEntityAddressMutation } from "@mutations/entity";
import { useEntity } from "@components/entity/useEntity";
import {
    PlainModal,
    StandardModalHeader,
} from "@components/general/Modal/Modal";
import { useToaster } from "@components/general/ToastMessages/useToaster";
import { submitHelper } from "@helpers/form";
import { AddressForm, getUSAddressSchema } from "@components/forms/AddressForm";
import { USAddress } from "@common/types/embeddable/usAddress";
import { Address } from "@common/types/embeddable/address";
import { EditModalFooter } from "./EditModalFooter";

function getRelevantAddressFields(address: Partial<USAddress>) {
    return {
        street: address.street ?? "",
        city: address.city ?? "",
        state: address.state ?? "",
        postalCode: address.postalCode ?? "",
    };
}

interface Props {
    show: boolean;
    onClose(): void;
    address?: Partial<Address>;
}

export const UpdateAddressModal: React.FC<Props> = ({
    show,
    onClose,
    address,
}) => {
    const entity = useEntity();
    const mutation = useEntityAddressMutation(entity);

    const { error } = useToaster();

    const form: FormikConfig<USAddress> = {
        initialValues: getRelevantAddressFields((address as USAddress) ?? {}),
        validationSchema: object().shape(
            getUSAddressSchema(!entity.isPersonal),
        ),
        onSubmit: submitHelper({
            handler: async (newAddress) => {
                try {
                    await mutation.mutateAsync(newAddress);
                    onClose();
                } catch {
                    error("Failed to update profile");
                }
            },
        }),
    };

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>
                <h4 className="m-0">Update Address</h4>
            </StandardModalHeader>
            <Formik {...form}>
                {({ isValid, isSubmitting }) => (
                    <Form>
                        <fieldset>
                            <AddressForm prefix="" fieldSize="sm" />
                        </fieldset>

                        <EditModalFooter
                            onClose={onClose}
                            isValid={isValid}
                            isSubmitting={isSubmitting}
                        />
                    </Form>
                )}
            </Formik>
        </PlainModal>
    );
};
